import { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { ChevronLeft, Loader2 } from "lucide-react";

const Field = ({ label, children }) => (
  <div className="flex flex-col gap-1.5">
    <Label className="text-sm font-medium text-foreground">{label}</Label>
    {children}
  </div>
);

export default function NewCase() {
  const { role } = useParams();
  const navigate = useNavigate();
  const isPatient = role === "patient";
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    name: "",
    age: "",
    sex: "",
    chief_complaint: "",
    hpi: "",
    past_history: "",
    medications: "",
    family_history: "",
    social_history: "",
  });

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async () => {
    if (!form.name.trim() || !form.chief_complaint.trim()) {
      toast.error("Name and main complaint are required");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        role,
        patient: { name: form.name, age: form.age, sex: form.sex },
        chief_complaint: form.chief_complaint,
        hpi: form.hpi,
        past_history: form.past_history,
        medications: form.medications,
        family_history: form.family_history,
        social_history: form.social_history,
      };
      const created = await api.createCase(payload);
      toast.success("Case created");
      navigate(`/${role}/cases/${created.id}`);
    } catch (e) {
      toast.error("Could not create case");
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen px-6 pb-24 pt-10">
      <div className="mx-auto max-w-2xl">
        <button
          data-testid="back-to-cases"
          onClick={() => navigate(`/${role}/cases`)}
          className="mb-6 inline-flex items-center gap-1 text-sm font-medium text-muted-foreground transition-colors hover:text-foreground"
        >
          <ChevronLeft className="h-4 w-4" /> Back to cases
        </button>

        <h1 className="font-heading text-3xl font-semibold text-foreground sm:text-4xl">
          {isPatient ? "Tell us about yourself" : "New Patient Case"}
        </h1>
        <p className="mt-2 text-sm text-muted-foreground">
          {isPatient
            ? "Describe your situation in plain language — there are no wrong answers."
            : "Capture the presenting history. The assistant will build on this."}
        </p>

        <Card className="mt-8 flex flex-col gap-5 p-7">
          <div className="grid grid-cols-1 gap-5 sm:grid-cols-3">
            <Field label="Name">
              <Input data-testid="input-name" value={form.name} onChange={set("name")} placeholder="Full name" />
            </Field>
            <Field label="Age">
              <Input data-testid="input-age" value={form.age} onChange={set("age")} placeholder="e.g. 58" />
            </Field>
            <Field label="Sex">
              <Input data-testid="input-sex" value={form.sex} onChange={set("sex")} placeholder="M / F / Other" />
            </Field>
          </div>

          <Field label={isPatient ? "What is the main problem bringing you here?" : "Chief complaint"}>
            <Textarea
              data-testid="input-chief-complaint"
              value={form.chief_complaint}
              onChange={set("chief_complaint")}
              placeholder={isPatient ? "e.g. A lump in my breast for 2 months" : "e.g. Painless breast lump x 8 weeks"}
              rows={2}
            />
          </Field>

          <Field label={isPatient ? "Tell us more about how it started and changed" : "History of presenting illness"}>
            <Textarea data-testid="input-hpi" value={form.hpi} onChange={set("hpi")} rows={3}
              placeholder="Onset, duration, progression, associated symptoms, weight loss, pain…" />
          </Field>

          <Field label={isPatient ? "Other illnesses / surgeries you've had" : "Past medical history"}>
            <Textarea data-testid="input-past-history" value={form.past_history} onChange={set("past_history")} rows={2} />
          </Field>

          <Field label={isPatient ? "Medicines you take" : "Current medications"}>
            <Textarea data-testid="input-medications" value={form.medications} onChange={set("medications")} rows={2} />
          </Field>

          <Field label={isPatient ? "Cancer or illness in your family" : "Family history"}>
            <Textarea data-testid="input-family-history" value={form.family_history} onChange={set("family_history")} rows={2} />
          </Field>

          <Field label={isPatient ? "Smoking, alcohol, work exposures" : "Social history"}>
            <Textarea data-testid="input-social-history" value={form.social_history} onChange={set("social_history")} rows={2} />
          </Field>

          <Button data-testid="create-case-submit" onClick={submit} disabled={saving} className="mt-2 gap-2">
            {saving && <Loader2 className="h-4 w-4 animate-spin" />}
            {saving ? "Creating…" : "Create case & continue"}
          </Button>
        </Card>
      </div>
    </div>
  );
}
